import React, { useState, useEffect, useRef } from 'react';
import {
  Palette,
  Image,
  Save,
  AlertCircle,
  CheckCircle,
  Loader2,
  Type,
  FileText,
  CreditCard,
  Eye,
  Upload,
  Trash2
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import firmService from '../../services/firmService';

const BrandingSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [firm, setFirm] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    primary_color: '#586994',
    secondary_color: '#181c2e',
    display_name: '',
    tagline: '',
    email_footer: '',
    hide_powered_by: false,
    invoice_payment_instructions: '',
    invoice_default_notes: ''
  });

  const loadFirmBranding = async () => {
    setLoading(true);
    setError('');
    try {
      const firmData = await firmService.getFirm(user.active_firm_id);
      setFirm(firmData);
      setLogoPreviewUrl(firmData.logo_url || '');
      setFormData({
        primary_color: firmData.primary_color || '#586994',
        secondary_color: firmData.secondary_color || '#181c2e',
        display_name: firmData.display_name || '',
        tagline: firmData.tagline || '',
        email_footer: firmData.email_footer || '',
        hide_powered_by: firmData.hide_powered_by || false,
        invoice_payment_instructions: firmData.invoice_payment_instructions || '',
        invoice_default_notes: firmData.invoice_default_notes || ''
      });
    } catch (err) {
      setError('Failed to load branding settings');
      console.error('Error loading branding:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.active_firm_id) {
      loadFirmBranding();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.active_firm_id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setSuccess('');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a JPEG, PNG, GIF, WebP, or SVG image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    setError('');
    setSuccess('');

    try {
      const result = await firmService.uploadLogo(user.active_firm_id, file);
      setLogoPreviewUrl(result.logo_display_url);
      setSuccess('Logo uploaded successfully');
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLogoRemove = async () => {
    setError('');
    setSuccess('');
    try {
      await firmService.deleteLogo(user.active_firm_id);
      setLogoPreviewUrl('');
      setSuccess('Logo removed');
    } catch (err) {
      console.error('Error removing logo:', err);
      setError(err.response?.data?.detail || 'Failed to remove logo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Only send non-empty values, convert empty strings to null
      const updateData = {};
      Object.entries(formData).forEach(([key, value]) => {
        if (typeof value === 'boolean') {
          updateData[key] = value;
        } else if (value && value.trim()) {
          updateData[key] = value.trim();
        } else {
          updateData[key] = null;
        }
      });

      await firmService.updateFirm(user.active_firm_id, updateData);
      setSuccess('Branding settings saved successfully');
    } catch (err) {
      console.error('Error saving branding:', err);
      setError(err.response?.data?.detail || 'Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
        <span className="ml-2 text-gray-600">Loading branding settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-2">
          <Palette className="w-6 h-6 text-yellow-500" />
          <h2 className="text-xl font-semibold text-gray-900">Firm Branding</h2>
        </div>
        <p className="text-gray-600">
          Customize your firm's branding for document signing, invoices, and client communications.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-700">{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo & Colors */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Image className="w-5 h-5 text-gray-500" />
            Logo & Colors
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Firm Logo
              </label>

              {logoPreviewUrl ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <img
                    src={logoPreviewUrl}
                    alt="Firm logo"
                    className="max-h-16 object-contain mb-3"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-1.5"
                    >
                      {uploadingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      Replace
                    </button>
                    <button
                      type="button"
                      onClick={handleLogoRemove}
                      className="px-3 py-1.5 text-sm text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-1.5"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-yellow-400 hover:bg-yellow-50/30 transition-colors"
                >
                  {uploadingLogo ? (
                    <Loader2 className="w-8 h-8 mx-auto text-yellow-500 animate-spin mb-2" />
                  ) : (
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  )}
                  <p className="text-sm text-gray-600">
                    {uploadingLogo ? 'Uploading...' : 'Click to upload your firm logo'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PNG, JPG, SVG, or WebP. Max 5MB. Recommended: 200x60px.
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                    placeholder="#586994"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Used for headers, buttons, and accents
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secondary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.secondary_color}
                    onChange={(e) => handleChange('secondary_color', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-mono"
                    placeholder="#181c2e"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Used for secondary elements and accents
                </p>
              </div>
            </div>

            {/* Color Preview */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Color Preview:</p>
              <div className="flex gap-4">
                <div
                  className="w-24 h-12 rounded-lg shadow-sm flex items-center justify-center text-white text-xs font-medium"
                  style={{ backgroundColor: formData.primary_color }}
                >
                  Primary
                </div>
                <div
                  className="w-24 h-12 rounded-lg shadow-sm flex items-center justify-center text-white text-xs font-medium"
                  style={{ backgroundColor: formData.secondary_color }}
                >
                  Secondary
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Display Name & Tagline */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Type className="w-5 h-5 text-gray-500" />
            Display Name & Tagline
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Display Name
              </label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => handleChange('display_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder={firm?.name || 'Your Firm Name'}
              />
              <p className="mt-1 text-xs text-gray-500">
                Display name for branding. Leave blank to use "{firm?.name}"
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tagline
              </label>
              <input
                type="text"
                value={formData.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Professional Legal Services"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional tagline displayed under your logo on invoices
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Settings */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-gray-500" />
            Invoice Branding
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Instructions
              </label>
              <textarea
                rows={4}
                value={formData.invoice_payment_instructions}
                onChange={(e) => handleChange('invoice_payment_instructions', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Bank: Example Bank&#10;Account: 1234567890&#10;Routing: 987654321&#10;&#10;Or pay online at..."
              />
              <p className="mt-1 text-xs text-gray-500">
                Payment instructions shown on invoices (bank details, accepted payment methods)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Invoice Notes
              </label>
              <textarea
                rows={3}
                value={formData.invoice_default_notes}
                onChange={(e) => handleChange('invoice_default_notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Thank you for your business. Payment is due within 30 days."
              />
              <p className="mt-1 text-xs text-gray-500">
                Default notes/terms shown at the bottom of invoices
              </p>
            </div>
          </div>
        </div>

        {/* Email & Document Signing */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" />
            Email & Document Signing
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Footer
              </label>
              <textarea
                rows={3}
                value={formData.email_footer}
                onChange={(e) => handleChange('email_footer', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Your Firm Name | 123 Main St, City, State 12345 | (555) 123-4567"
              />
              <p className="mt-1 text-xs text-gray-500">
                Custom footer text for outgoing emails
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="hide_powered_by"
                checked={formData.hide_powered_by}
                onChange={(e) => handleChange('hide_powered_by', e.target.checked)}
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
              />
              <label htmlFor="hide_powered_by" className="ml-2 text-sm text-gray-700">
                Hide "Powered by" attribution on document signing pages
              </label>
            </div>
          </div>
        </div>

        {/* Preview Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-500" />
            Branding Preview
          </h3>

          <div className="border rounded-lg overflow-hidden">
            {/* Header Preview */}
            <div
              className="p-6 text-white"
              style={{ backgroundColor: formData.primary_color }}
            >
              {logoPreviewUrl ? (
                <img
                  src={logoPreviewUrl}
                  alt="Logo"
                  className="h-10 object-contain mb-2"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <h4 className="text-xl font-semibold">
                  {formData.display_name || firm?.name || 'Your Firm Name'}
                </h4>
              )}
              {formData.tagline && (
                <p className="text-sm opacity-90">{formData.tagline}</p>
              )}
            </div>

            {/* Content Preview */}
            <div className="p-6 bg-gray-50">
              <p className="text-sm text-gray-600">
                This is how your branding will appear on invoices, document signing pages, and client emails.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Branding
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BrandingSettings;
